import { useDebounce } from "hooks/useDebounce"
import { useCallback, useEffect, useState } from "react"
import { Flex, IconButton, TextInput } from "@strapi/design-system"
import { Trash } from "@strapi/icons"
import { rgbToHex, tintRgbTohex } from "./helpers"

import chroma from "chroma-js"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { HexColorPicker } from "react-colorful"

export { tintRgbTohex, rgbToHex }

interface ColorItemProps {
  value: string
  onChange: (value: string) => void
  onDelete?: () => void
  disabledDelete?: boolean
}

interface RgbInputsProps {
  rgb: { r: string; g: string; b: string }
  onChange: (channel: "r" | "g" | "b", val: string) => void
}

const RgbInputs: React.FC<RgbInputsProps> = ({ rgb, onChange }) => (
  <div className="flex flex-col items-center gap-4 w-22">
    <div className="flex items-center gap-1">
      <span>R</span>
      <TextInput
        value={rgb.r}
        onChange={(e) => onChange("r", e.target.value)}
        placeholder="0-255"
        aria-label={"R"}
      />
    </div>
    <div className="flex items-center gap-1">
      <span>G</span>
      <TextInput
        value={rgb.g}
        onChange={(e) => onChange("g", e.target.value)}
        placeholder="0-255"
        aria-label={"G"}
      />
    </div>
    <div className="flex items-center gap-1">
      <span>B</span>
      <TextInput
        value={rgb.b}
        onChange={(e) => onChange("b", e.target.value)}
        placeholder="0-255"
        aria-label={"B"}
      />
    </div>
  </div>
)

export const ColorItem = ({ value, onChange, onDelete }: ColorItemProps) => {
  const [color, setColor] = useState<string>(value)

  const debouncedValue = useDebounce(color, 200)

  useEffect(() => {
    if (debouncedValue !== value) onChange(debouncedValue)
  }, [debouncedValue, onChange, value])

  const c = chroma.valid(color) ? chroma(color) : undefined
  const [popoverActive, setPopoverActive] = useState(false)
  const [colorHex, setColorHex] = useState<string>(c ? c.hex("rgb") : "")

  const [rgb, setRgb] = useState(() => {
    if (!c) return { r: "", g: "", b: "" }
    const [r, g, b] = c.rgb()
    return { r: r.toString(), g: g.toString(), b: b.toString() }
  })

  useEffect(() => {
    setColorHex(color)
    const [r, g, b] = chroma(color).rgb()
    setRgb({ r: r.toString(), g: g.toString(), b: b.toString() })
  }, [color])

  const handleHexChange = (v: string) => {
    if (v[0] !== "#") v = "#" + v
    if (v.length > 7) v = v.slice(0, 7)
    setColorHex(v)
    if (chroma.valid(v)) {
      const validChroma = chroma(v)
      const [r, g, b] = validChroma.rgb()
      setRgb({ r: r.toString(), g: g.toString(), b: b.toString() })
      setColor(v)
    }
  }

  const handleRgbChange = (channel: "r" | "g" | "b", val: string) => {
    if (val.length > 3) val = val.slice(0, 3)
    const next = { ...rgb, [channel]: val }
    setRgb(next)
    const r = parseInt(next.r)
    const g = parseInt(next.g)
    const b = parseInt(next.b)
    if ([r, g, b].some((n) => isNaN(n) || n < 0 || n > 255)) {
      setColor("")
      setColorHex("")
      return
    }
    const hex = chroma.rgb(r, g, b).hex("rgb")
    setColorHex(hex)
    setColor(hex)
  }

  const togglePopoverActive = useCallback(() => setPopoverActive((a) => !a), [])
  return (
    <Flex gap={6} position="relative">
      <Popover open={popoverActive} onOpenChange={togglePopoverActive}>
        <PopoverTrigger asChild>
          <button className="text-left  flex items-center justify-between">
            <div
              className="w-10 h-10 rounded-sm relative  " // Added border here
              style={{ backgroundColor: c?.css() || "transparent" }}
            >
              {!c?.css() && (
                <div className="absolute inset-0 flex items-center justify-center rounded-sm !border !border-red-500 ">
                  <div className="w-full h-0.5 bg-red-500 transform rotate-45 absolute"></div>
                  <div className="w-full h-0.5 bg-red-500 transform -rotate-45 absolute"></div>
                </div>
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          style={{
            backgroundColor: "#212134",
          }}
          className="!p-2 bg-gray-1 text-gray-200 !border-gray-600 !border-[1px] !border-solid rounded-md"
        >
          <div className="flex items-center gap-4 ">
            <RgbInputs rgb={rgb} onChange={handleRgbChange} />
            <HexColorPicker
              className={"!mb-2"}
              color={chroma.valid(color) ? color : "#FF0000"}
              onChange={setColor}
            />
          </div>
          <TextInput
            value={colorHex}
            aria-label="input"
            onChange={(e) => handleHexChange(e.target.value)}
            placeholder="#FFFFFF"
          />
        </PopoverContent>
      </Popover>
      {onDelete && <IconButton icon={<Trash width={22} />} onClick={onDelete}></IconButton>}
    </Flex>
  )
}
